///
/// EarthBrowser
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Desktop data dock: an icon rail on the right edge with one button per
/// dataset that has a table (docked windows, plus icons derived from
/// layers that are on the globe), and a width-resizable panel to its left
/// showing the active browser. Collapsed, only the rail remains, so the
/// bottom edge stays free for the time control and legends and the Layers
/// panel on the left never overlaps it.
///

import { useLayerVisibility } from "@/components/controls/layers-panel/useLayerVisibility"
import { cDockEdgeInset, cDockRailBottomReserve, cDockRailWidth, effectiveDockWidth, useViewportWidth } from "@/hooks/useBottomChromeOffset"
import {
	closeDrawerTab,
	openDrawer,
	setActiveDrawerTab,
	toggleDrawer,
	undockTabToWindow,
	type DatasetBrowserWindowState,
} from "@/redux/dialogSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { setPersistedDockWidth } from "@/redux/userInterfaceSlice"
import { cn } from "@/utils/utils"
import { PanelRightClose, PanelRightOpen, PictureInPicture2, X } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { cDatasetWindowMeta } from "./datasetWindowMeta"
import { useDockItems, type DockItem } from "./useDockItems"
import { useDockKeyboardShortcuts } from "./useDockKeyboardShortcuts"
import { useDockWindowVisibility } from "./useDockWindowVisibility"
import { useRailDrag } from "./useRailDrag"
import { useWindowContent } from "./WindowContentRegistry"

// Below the top-right control row (top-2 + h-9)
const DOCK_TOP = 52

const railButtonClass = cn(
	"relative flex items-center justify-center w-9 h-9 rounded-lg",
	"transition-colors duration-200",
	"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
)

export function RightDock() {
	const dispatch = useAppDispatch()
	const { isOpen, tabs, activeTabId } = useAppSelector(state => state.dialog.bottomDrawer)
	const datasetWindows = useAppSelector(state => state.dialog.datasetWindows)
	const persistedWidth = useAppSelector(state => state.userInterface.bottomDrawer.dockWidth)
	const viewportWidth = useViewportWidth()
	const items = useDockItems()
	const { getVisibility, getToggle } = useDockWindowVisibility()
	const { openLayerTable } = useLayerVisibility()
	useDockKeyboardShortcuts()

	const activeWindow: DatasetBrowserWindowState | null =
		activeTabId && tabs.some(t => t.windowId === activeTabId) ? datasetWindows[activeTabId] ?? null : null
	const activeContent = useWindowContent(activeWindow)

	// Drag rail items to reorder, or off the rail to float
	const tabOrder = useMemo(() => tabs.map(t => t.windowId), [tabs])
	const { drag, localOrder, registerItem, handlePointerDown, shouldSuppressClick } = useRailDrag(tabOrder, datasetWindows)
	const orderedItems = useMemo((): DockItem[] => {
		if (!localOrder) return items
		const windowItems = new Map<string, DockItem>(items.filter(i => i.kind === "window").map(i => [i.id, i]))
		const reordered: DockItem[] = []
		for (const id of localOrder) {
			const item = windowItems.get(id)
			if (item) reordered.push(item)
		}
		return [...reordered, ...items.filter(i => i.kind === "layer")]
	}, [items, localOrder])
	const dragWindow = drag ? datasetWindows[drag.windowId] : undefined
	const dragMeta = dragWindow?.windowType ? cDatasetWindowMeta[dragWindow.windowType] : undefined

	// Panel width: local while dragging, persisted on release
	const [localWidth, setLocalWidth] = useState(persistedWidth)
	const [isResizing, setIsResizing] = useState(false)
	const resizeStartXRef = useRef(0)
	const resizeStartWidthRef = useRef(0)

	useEffect(() => {
		if (!isResizing) setLocalWidth(persistedWidth)
	}, [persistedWidth, isResizing])

	const handleResizeStart = useCallback((e: React.PointerEvent) => {
		e.preventDefault()
		e.stopPropagation()
		setIsResizing(true)
		resizeStartXRef.current = e.clientX
		resizeStartWidthRef.current = localWidth
	}, [localWidth])

	useEffect(() => {
		if (!isResizing) return
		const onMove = (e: PointerEvent) => {
			// The handle is on the panel's left edge: dragging left widens
			const proposed = resizeStartWidthRef.current + (resizeStartXRef.current - e.clientX)
			setLocalWidth(effectiveDockWidth(proposed, window.innerWidth))
		}
		const onUp = () => {
			setIsResizing(false)
			setLocalWidth(width => {
				dispatch(setPersistedDockWidth(Math.round(width)))
				return width
			})
		}
		document.addEventListener("pointermove", onMove)
		document.addEventListener("pointerup", onUp)
		return () => {
			document.removeEventListener("pointermove", onMove)
			document.removeEventListener("pointerup", onUp)
		}
	}, [isResizing, dispatch])

	const handleItemClick = useCallback((item: DockItem) => {
		if (shouldSuppressClick()) return
		if (item.kind === "layer") {
			// Runs the layer's browser command, which creates and docks the
			// window; the derived icon becomes explicit in place
			openLayerTable(item.layer)
			return
		}
		if (item.id === activeTabId) {
			dispatch(toggleDrawer())
			return
		}
		dispatch(setActiveDrawerTab(item.id))
		if (!isOpen) dispatch(openDrawer())
	}, [shouldSuppressClick, openLayerTable, activeTabId, isOpen, dispatch])

	const handleToggle = useCallback(() => {
		if (!isOpen && !activeWindow && tabs.length > 0) {
			dispatch(setActiveDrawerTab(tabs[0].windowId))
		}
		dispatch(toggleDrawer())
	}, [isOpen, activeWindow, tabs, dispatch])

	const handleFloat = useCallback(() => {
		if (!activeWindow) return
		const width = activeWindow.width || 600
		const height = activeWindow.height || 400
		dispatch(undockTabToWindow({
			windowId: activeWindow.id,
			x: Math.max(8, Math.round((window.innerWidth - width) / 2)),
			y: Math.max(8, Math.round((window.innerHeight - height) / 2)),
		}))
	}, [activeWindow, dispatch])

	if (items.length === 0) return null

	const panelOpen = isOpen && activeWindow !== null
	const activeMeta = activeWindow?.windowType ? cDatasetWindowMeta[activeWindow.windowType] : undefined
	const activeVisible = activeWindow ? getVisibility(activeWindow) : undefined
	const activeToggle = activeWindow ? getToggle(activeWindow) : undefined
	const canFloat = activeWindow?.dockMode !== "dock-only"

	return (
		<div
			// items-start: the panel takes its height from its content (a
			// four-row hurricane table doesn't need a full-height panel) and
			// max-h-full caps it at the viewport, where the table scrolls.
			// pointer-events-none: this box spans the whole right edge and
			// would otherwise swallow clicks/touches on the camera stack in
			// the corner below the rail; the panel and rail re-enable them
			className="fixed z-[950] flex items-start gap-2 pointer-events-none"
			style={{
				// Below the top control row, pushed down by the status bar /
				// Dynamic Island inset in the native apps
				top: `calc(${DOCK_TOP}px + var(--safe-area-inset-top))`,
				bottom: `calc(${cDockEdgeInset}px + var(--safe-area-inset-bottom))`,
				right: `calc(${cDockEdgeInset}px + var(--safe-area-inset-right))`,
			}}
			data-data-dock
		>
			{panelOpen && activeWindow && (
				<div
					className={cn(
						"relative flex flex-col max-h-full pointer-events-auto",
						"bg-black/40 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl",
						isResizing && "select-none"
					)}
					style={{ width: effectiveDockWidth(localWidth, viewportWidth) }}
				>
					{/* Width resize handle */}
					<div
						className={cn(
							"absolute top-0 bottom-0 -left-1 w-2 cursor-ew-resize touch-none z-10",
							"after:absolute after:top-1/2 after:-translate-y-1/2 after:left-[3px] after:h-10 after:w-0.5 after:rounded-full",
							isResizing ? "after:bg-cyan-400/70" : "after:bg-white/0 hover:after:bg-white/30"
						)}
						onPointerDown={handleResizeStart}
						role="separator"
						aria-orientation="vertical"
						aria-label="Resize data dock"
					/>

					{/* Header */}
					<div className="flex items-center gap-2 h-10 px-2 border-b border-white/10 flex-shrink-0">
						{activeToggle !== undefined && (
							<button
								onClick={activeToggle}
								className={cn(
									"flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-colors",
									"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/50",
									activeVisible
										? "bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30"
										: "bg-white/5 text-white/40 hover:bg-white/10"
								)}
								aria-label={activeVisible ? "Hide from globe" : "Show on globe"}
								title={activeVisible ? "Hide from globe" : "Show on globe"}
							>
								<div className="w-2.5 h-2.5 rounded-full bg-current" />
							</button>
						)}
						{activeMeta && (
							<activeMeta.Icon className={cn("w-4 h-4 flex-shrink-0", activeMeta.colorClass)} />
						)}
						<h2 className="flex-1 min-w-0 text-sm font-semibold text-white truncate">
							{activeWindow.title}
						</h2>
						{canFloat && (
							<button
								onClick={handleFloat}
								className="p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
								aria-label="Float as a window"
								title="Float as a window"
							>
								<PictureInPicture2 className="w-4 h-4" />
							</button>
						)}
						<button
							onClick={() => dispatch(closeDrawerTab(activeWindow.id))}
							className="p-1.5 rounded-lg text-white/60 hover:text-red-400 hover:bg-red-500/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/50"
							aria-label="Close"
							title="Close"
						>
							<X className="w-4 h-4" />
						</button>
					</div>

					{/* Keyed by window id so switching items remounts the content;
					    two browsers of one component type would otherwise share state */}
					{/* Flex column so the browser's own table scroller shrinks to the
					    cap instead of this div scrolling (keeps the sticky header) */}
					<div key={activeWindow.id} className="flex-1 min-h-[220px] flex flex-col overflow-auto rounded-b-xl">
						{activeContent}
					</div>
				</div>
			)}

			{/* Rail */}
			<div
				className={cn(
					"self-start flex flex-col items-center gap-1 p-1.5 pointer-events-auto",
					"bg-black/40 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl",
					"overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600"
				)}
				// Capped short of the bottom so the camera stack keeps the corner
				style={{ width: cDockRailWidth, maxHeight: `calc(100% - ${cDockRailBottomReserve}px)` }}
				role="tablist"
				aria-orientation="vertical"
				aria-label="Data dock"
			>
				<button
					onClick={handleToggle}
					className={cn(railButtonClass, "text-white/50 hover:text-white hover:bg-white/10")}
					aria-label={panelOpen ? "Collapse data dock" : "Expand data dock"}
					title={panelOpen ? "Collapse data dock (⌘`)" : "Expand data dock (⌘`)"}
					disabled={tabs.length === 0}
				>
					{panelOpen ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
				</button>
				<div className="w-6 h-px bg-white/15 my-0.5" />

				{orderedItems.map(item => {
					if (item.kind === "layer") {
						return (
							<button
								key={item.id}
								onClick={() => handleItemClick(item)}
								className={cn(railButtonClass, "opacity-50 hover:opacity-100 hover:bg-white/10")}
								role="tab"
								aria-selected={false}
								aria-label={`Open ${item.layer.name} table`}
								title={`Open ${item.layer.name} table`}
							>
								{item.layer.icon}
							</button>
						)
					}

					const meta = item.window.windowType ? cDatasetWindowMeta[item.window.windowType] : undefined
					const isActive = item.id === activeTabId
					const visible = getVisibility(item.window)
					const isDragging = drag?.windowId === item.id
					return (
						<button
							key={item.id}
							ref={el => registerItem(item.id, el)}
							onClick={() => handleItemClick(item)}
							onPointerDown={e => handlePointerDown(item.id, e)}
							className={cn(
								railButtonClass,
								"touch-none",
								isActive
									? "bg-white/15 ring-1 ring-white/30 text-white"
									: "text-white/80 hover:bg-white/10 hover:text-white",
								isDragging && "opacity-40"
							)}
							role="tab"
							aria-selected={isActive}
							aria-label={item.window.title}
							title={`${item.window.title} — drag to reorder, drag left to float`}
						>
							{meta ? (
								<meta.Icon className={cn("w-4 h-4", meta.colorClass)} />
							) : (
								<span className="text-xs font-semibold">{item.window.title.slice(0, 1)}</span>
							)}
							{visible !== undefined && (
								<span
									className={cn(
										"absolute bottom-1 right-1 w-1.5 h-1.5 rounded-full",
										visible ? "bg-cyan-400" : "bg-white/30"
									)}
									aria-hidden
								/>
							)}
						</button>
					)
				})}
			</div>

			{/* Drag ghost: portaled so position:fixed is viewport-relative */}
			{drag && dragWindow && createPortal(
				<div
					className={cn(
						"fixed pointer-events-none z-[9999] flex items-center gap-2 px-3 py-1.5 rounded-lg",
						"bg-slate-800/90 backdrop-blur-sm border border-slate-500/50 shadow-xl text-xs font-medium text-white",
						drag.floating && "ring-2 ring-cyan-400/50"
					)}
					style={{ left: drag.x - 18, top: drag.y - 18 }}
				>
					{dragMeta && <dragMeta.Icon className={cn("w-4 h-4", dragMeta.colorClass)} />}
					<span>{dragWindow.title}</span>
					{drag.floating && <span className="text-[10px] text-cyan-400 whitespace-nowrap">Release to float</span>}
				</div>,
				document.body
			)}
		</div>
	)
}
